/**
 * KommunikationCardContent Component
 * Displays Kommunikation (Communication) data in a card layout
 * Shows top 3 channels and Kanal-Produkt-Fit scatter chart
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalysisChart } from '@/components/charts/AnalysisChart/AnalysisChart';
import { getEffectivenessColor } from '@/types/kommunikation';
import type { KommunikationFullData } from '@/types/kommunikation';
import type { ChartConfig } from '@/types/api';

export interface KommunikationCardContentProps {
  data: KommunikationFullData;
}

/**
 * Card content for Kommunikation displaying top channels and scatter chart
 */
export function KommunikationCardContent({
  data,
}: KommunikationCardContentProps) {
  const { t } = useTranslation();
  const { main } = data;

  // Get top 3 channels for the card
  const topChannels = main.Kommunikation_Top_Kanäle?.slice(0, 3) || [];

  // Create scatter chart config for Kanal-Produkt-Fit
  const scatterChartConfig = useMemo((): ChartConfig | null => {
    const matrix = main.Kommunikation_Produkt_Fit_Matrix;
    if (!matrix || !matrix.data || matrix.data.length === 0) {
      return null;
    }

    // Prepare series data for ApexCharts scatter
    const seriesData = matrix.data.map(point => ({
      x: point.x_value,
      y: point.y_value,
    }));

    return {
      type: 'scatter',
      series: [{
        name: 'Kanäle',
        data: seriesData,
      }] as unknown as ChartConfig['series'],
      options: {
        chart: {
          type: 'scatter',
          zoom: { enabled: false },
          toolbar: { show: false },
        },
        grid: {
          padding: {
            top: 0,
            right: 10,
            bottom: 0,
            left: 10,
          },
        },
        xaxis: {
          min: matrix.encoding.x.start,
          max: matrix.encoding.x.end,
          tickAmount: 5,
          title: {
            text: t('results.cards.kommunikation.card.productFit'),
            style: { fontSize: '10px', color: '#32373c' },
          },
          labels: {
            style: { fontSize: '9px', colors: '#32373c' },
          },
        },
        yaxis: {
          min: matrix.encoding.y.start,
          max: matrix.encoding.y.end,
          tickAmount: 5,
          title: {
            text: t('results.cards.kommunikation.card.effectiveness'),
            style: { fontSize: '10px', color: '#32373c' },
          },
          labels: {
            style: { fontSize: '9px', colors: '#32373c' },
          },
        },
        colors: ['#FC03B0'],
        markers: {
          size: 8,
          colors: ['#FC03B0'],
          strokeColors: '#fff',
          strokeWidth: 2,
          hover: { size: 10 },
        },
        tooltip: {
          custom: ({ dataPointIndex }: { dataPointIndex: number }) => {
            const point = matrix.data[dataPointIndex];
            return `<div class="p-2 bg-white shadow rounded text-xs">
              <strong>${point.name}</strong><br/>
              Produkt-Fit: ${point.x_value}<br/>
              Effektivität: ${point.y_value}
            </div>`;
          },
        },
        legend: { show: false },
      } as ChartConfig['options'],
    };
  }, [main.Kommunikation_Produkt_Fit_Matrix, t]);

  return (
    <div className="space-y-3">
      {/* Top 3 Channels - Podium Layout */}
      <div>
        <p className="text-xs font-medium text-[var(--color-charcoal)]/60 mb-2">
          {t('results.cards.kommunikation.card.topChannels')}
        </p>
        <div className="flex items-end justify-center gap-2">
          {/* 1st Place - Left (Tallest) */}
          {topChannels[0] && (
            <div className="flex flex-col items-center flex-1">
              <div className="text-center mb-1">
                <p className="text-xs font-semibold text-[var(--color-charcoal)] truncate max-w-[80px]">
                  {topChannels[0].Name}
                </p>
                <span
                  className="text-[10px] font-bold"
                  style={{ color: getEffectivenessColor(topChannels[0].Wirkungsgrad) }}
                >
                  {topChannels[0].Wirkungsgrad}/10
                </span>
              </div>
              <div
                className="w-full rounded-t-[var(--radius-sm)] flex items-end justify-center pb-1"
                style={{
                  height: '64px',
                  background: 'linear-gradient(135deg, #FC03B0, #FF6803)',
                }}
              >
                <span className="text-white font-bold text-xl">1</span>
              </div>
            </div>
          )}

          {/* 2nd Place - Center */}
          {topChannels[1] && (
            <div className="flex flex-col items-center flex-1">
              <div className="text-center mb-1">
                <p className="text-xs font-medium text-[var(--color-charcoal)] truncate max-w-[80px]">
                  {topChannels[1].Name}
                </p>
                <span
                  className="text-[10px] font-bold"
                  style={{ color: getEffectivenessColor(topChannels[1].Wirkungsgrad) }}
                >
                  {topChannels[1].Wirkungsgrad}/10
                </span>
              </div>
              <div
                className="w-full rounded-t-[var(--radius-sm)] flex items-end justify-center pb-1"
                style={{
                  height: '48px',
                  background: 'linear-gradient(135deg, #E8148A, #FC03B0)',
                }}
              >
                <span className="text-white font-bold text-lg">2</span>
              </div>
            </div>
          )}

          {/* 3rd Place - Right (Shortest) */}
          {topChannels[2] && (
            <div className="flex flex-col items-center flex-1">
              <div className="text-center mb-1">
                <p className="text-xs font-medium text-[var(--color-charcoal)] truncate max-w-[80px]">
                  {topChannels[2].Name}
                </p>
                <span
                  className="text-[10px] font-bold"
                  style={{ color: getEffectivenessColor(topChannels[2].Wirkungsgrad) }}
                >
                  {topChannels[2].Wirkungsgrad}/10
                </span>
              </div>
              <div
                className="w-full rounded-t-[var(--radius-sm)] flex items-end justify-center pb-1"
                style={{
                  height: '36px',
                  background: 'linear-gradient(135deg, #FF6803, #f97316)',
                }}
              >
                <span className="text-white font-bold text-base">3</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Scatter Chart - Kanal-Produkt-Fit */}
      <div className="pt-2 border-t border-[var(--glass-border)]">
        <p className="text-xs font-medium text-[var(--color-charcoal)]/60 mb-1">
          {t('results.cards.kommunikation.card.channelProductFit')}
        </p>
        <div className="-mx-2">
          {scatterChartConfig ? (
            <AnalysisChart
              config={scatterChartConfig}
              height={160}
            />
          ) : (
            <div className="h-[160px] flex items-center justify-center bg-[var(--glass-bg)] rounded-md mx-2">
              <span className="text-xs text-[var(--color-charcoal)]/50">
                {t('results.cards.kommunikation.card.noData')}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default KommunikationCardContent;
